import { Transition } from "@headlessui/react";
import { Head, router, useForm, usePage } from "@inertiajs/react";
import { Building2, Globe, TrendingUp } from "lucide-react";
import type { FormEventHandler } from "react";

import Heading from "@/components/heading";
import HeadingSmall from "@/components/heading-small";
import InputError from "@/components/input-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type {
  AvailableMonth,
  BreadcrumbItem,
  Organization,
  SharedData,
  UsagePeriod,
  UsageStats,
} from "@/types";

interface OrganizationSettingsProps {
  organization: Organization;
  usage_stats: UsageStats;
  available_months: AvailableMonth[];
  selected_period: UsagePeriod;
}

interface OrganizationForm {
  name: string;
  logo: File | null;
}

export default function OrganizationSettings({
  organization,
  usage_stats,
  available_months,
  selected_period,
}: OrganizationSettingsProps) {
  const { auth } = usePage<SharedData>().props;
  const routes = useOrganizationRoutes();

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Configuración de organización",
      href: routes.settings(),
    },
  ];

  const {
    data,
    setData,
    post,
    processing,
    errors,
    recentlySuccessful,
    transform,
  } = useForm<OrganizationForm>({
    name: organization.name,
    logo: null,
  });

  const canEdit =
    auth.groups.includes("owner") || auth.groups.includes("admin");

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    transform((formData) => ({
      _method: "PATCH",
      organization: {
        name: formData.name,
        logo: formData.logo,
      },
    }));

    post(routes.settings(), {
      preserveScroll: true,
      forceFormData: true,
    });
  };

  const handleMonthChange = (value: string) => {
    const [year, month] = value.split("-").map(Number);
    router.get(
      routes.settings(),
      { year, month },
      { preserveState: true, preserveScroll: true }
    );
  };

  const currentPeriodValue = `${selected_period.year}-${selected_period.month}`;

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Configuración de organización" />

      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <Heading
          title="Configuración de organización"
          description="Administra el nombre, logo y preferencias de tu organización"
        />

        <div className="mt-6 space-y-8">
          {/* Organization Profile Section */}
          <section className="space-y-6">
            <HeadingSmall
              title="Perfil de la organización"
              description="Información básica de tu organización"
            />

            <form onSubmit={submit} className="space-y-6">
              <div className="flex items-start gap-6">
                {organization.logo_url ? (
                  <img
                    src={organization.logo_url}
                    alt={organization.name}
                    className="h-20 w-20 shrink-0 rounded-lg border object-cover"
                  />
                ) : (
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border bg-muted">
                    <Building2 className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}

                <div className="flex-1 space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Nombre</Label>
                    <Input
                      id="name"
                      value={data.name}
                      onChange={(e) => setData("name", e.target.value)}
                      disabled={!canEdit}
                      required
                      placeholder="Nombre de la organización"
                    />
                    <InputError message={errors.name} />
                  </div>

                  {canEdit && (
                    <div className="grid gap-2">
                      <Label htmlFor="logo">Logo</Label>
                      <Input
                        id="logo"
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          setData("logo", e.target.files?.[0] ?? null)
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        JPG, PNG o GIF. Máximo 5MB.
                      </p>
                      <InputError message={errors.logo} />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border bg-muted/50 px-4 py-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">Slug: </span>
                  <code className="font-mono">{organization.slug}</code>
                </div>
              </div>

              {canEdit && (
                <div className="flex items-center gap-4">
                  <Button type="submit" disabled={processing}>
                    {processing ? "Guardando..." : "Guardar cambios"}
                  </Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-sm text-green-600">Guardado</p>
                  </Transition>
                </div>
              )}

              {!canEdit && (
                <p className="text-sm text-muted-foreground">
                  Solo los administradores pueden editar la configuración.
                </p>
              )}
            </form>
          </section>

          <Separator />

          {/* Public Catalog Section */}
          {canEdit && (
            <>
              <section className="space-y-6">
                <HeadingSmall
                  title="Catálogo público"
                  description="Acceso a productos, categorías y proveedores compartidos"
                />

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Globe className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Habilitar catálogo público</p>
                      <p className="text-sm text-muted-foreground">
                        {organization.public_catalog_enabled
                          ? "Ya tienes acceso a productos compartidos."
                          : "Activa para ver productos listos para usar."}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={organization.public_catalog_enabled ?? false}
                    disabled={organization.public_catalog_enabled}
                    onCheckedChange={(checked) => {
                      if (checked && !organization.public_catalog_enabled) {
                        router.patch(routes.settings(), {
                          organization: { public_catalog_enabled: true },
                        });
                      }
                    }}
                  />
                </div>

                {organization.public_catalog_enabled && (
                  <p className="text-sm text-amber-600">
                    Esta opción no se puede deshabilitar una vez activada.
                  </p>
                )}
              </section>

              <Separator />
            </>
          )}

          {/* Usage Stats Section */}
          <section className="space-y-6">
            <HeadingSmall
              title="Uso mensual"
              description="Consumo de créditos por acciones realizadas"
            />

            <div className="flex items-center gap-3">
              <Label htmlFor="month-select" className="shrink-0">
                Periodo:
              </Label>
              <Select value={currentPeriodValue} onValueChange={handleMonthChange}>
                <SelectTrigger id="month-select" className="w-48">
                  <SelectValue placeholder="Seleccionar mes" />
                </SelectTrigger>
                <SelectContent>
                  {available_months.map((m) => (
                    <SelectItem
                      key={`${m.year}-${m.month}`}
                      value={`${m.year}-${m.month}`}
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  Total de créditos consumidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold tracking-tight">
                  {usage_stats.total_credits.toFixed(2)}
                </p>
              </CardContent>
            </Card>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Acción</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Costo unitario</TableHead>
                    <TableHead className="text-right">Créditos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage_stats.breakdown.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="py-8 text-center text-muted-foreground"
                      >
                        Sin actividad en este periodo
                      </TableCell>
                    </TableRow>
                  ) : (
                    usage_stats.breakdown.map((item) => (
                      <TableRow key={item.action}>
                        <TableCell>{item.label}</TableCell>
                        <TableCell className="text-right">{item.count}</TableCell>
                        <TableCell className="text-right">
                          {item.unit_cost}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {item.credits.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
